import React, {useEffect, useState, memo} from "react";
import {createStyles, makeStyles} from "@material-ui/core/styles";
import {fs, isStaticSite} from '../electron-modules';
import {DraggableDialog, IconToolBar, ToolBarButton, ToolBarDivider} from "../components";
import { Viewer } from "../viewer/js/main";
import {
  FormHelperText,
  MenuItem,
  Select
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {dialog} from "../electron-modules";
import {
  pngFilter
} from "../file-extensions";
import slash from "slash";

const useStyles = makeStyles((theme) =>
  createStyles({
    profileContainer: {
      display: "flex",
      flexDirection: "column",
      height: "100%"
    },
    toolbarDropdown: {
      flex: 1,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1)
    },
    profileChart: {
      flex: 1,
      overflow: "hidden",
      padding: theme.spacing(2)
    }
  })
);

export const PointProfile = memo((props: any) => {
  const viewer = props.viewer as Viewer;
  const {open, setOpen} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [samplingType, setSamplingType] = useState('lowest');
  const [displayUnits, setDisplayUnits] = useState('m');

  const onRefModified = (container) => {
    if (!container) return;
    viewer.initProfileContainer(samplingType, displayUnits);
  };

  const reloadPointsProfile = () => {
    viewer.refreshPointProfile();
  };

  const resetPointsProfileView = () => {
    viewer.resetPointProfileView();
  };

  const savePointProfileImage = () => {
    if (isStaticSite) {
      savePointProfileImageBrowser();
    } else {
      savePointProfileImageElectron();
    }
  };

  const savePointProfileImageBrowser = () => {
    const imageBase64 = viewer.getPointProfileImage();
    const filename = viewer.projectName;
    let type = 'png';
    let name = `${filename}.${type}`;

    let element = document.createElement('a');
    element.setAttribute('href', imageBase64);
    if (name) {
      element.setAttribute('download', name);
    }
    element.style.display = 'none';
    document.body.appendChild(element);
    element.target = "_blank";
    element.click();
    document.body.removeChild(element);

    return;
  };

  const savePointProfileImageElectron = () => {
    dialog.showSaveDialog({
      defaultPath: viewer.projectName,
      filters: [...pngFilter],
    }).then(result => {
      if (result.canceled) {
        return;
      }

      if (result.filePath) {
        const filePath = slash(result.filePath);
        const imageBase64 = viewer.getPointProfileImage();
        let imageBase64Data = imageBase64.replace(/^data:image\/\w+;base64,/, '');

        const imageBuffer = Buffer.from(imageBase64Data, 'base64');
        fs.writeFileSync(filePath, imageBuffer, {encoding: "base64"});
        console.log(`Point Profile Image saved to ${filePath}`);
      }
    }).catch(error => {
      console.log(error);
    });
    return;
  };

  const updateUnits = (event) => {
    setDisplayUnits(event.target.value);
  };

  const updateSamplingType = (event) => {
    setSamplingType(event.target.value);
  };

  useEffect(() => {
    viewer?.setPointProfileSampling(samplingType);
  }, [viewer, samplingType]);

  useEffect(() => {
    viewer?.setPointProfileUnits(displayUnits);
  }, [viewer, displayUnits]);

  useEffect(() => {
    if (open) return;
    viewer?.resetPointProfile();
  }, [viewer, open]);

  return (
    <React.Fragment>
      <DraggableDialog
        variant={'resize'}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        title={t('profile.point-profile')}
      >
        <div className={classes.profileContainer} id="profile-container">

          <IconToolBar margin={1}>

            <div className={classes.toolbarDropdown}>
              <Select
                fullWidth
                value={displayUnits}
                onChange={updateUnits}
              >
                <MenuItem value={"m"}>{t('units.meters')}</MenuItem>
                <MenuItem value={"ft"}>{t('units.feet')}</MenuItem>
                <MenuItem value={"sft"}>{t('units.survey-feet')}</MenuItem>
              </Select>
              <FormHelperText>{t('profile.profile-units')}</FormHelperText>
            </div>

            <div className={classes.toolbarDropdown}>
              <Select
                fullWidth
                value={samplingType}
                onChange={updateSamplingType}
              >
                <MenuItem value={"highest"}>{t('profile.highest')}</MenuItem>
                <MenuItem value={"lowest"}>{t('profile.lowest')}</MenuItem>
                <MenuItem value={"all"}>{t('profile.all-points')}</MenuItem>
              </Select>
              <FormHelperText>{t('profile.profile-type')}</FormHelperText>
            </div>

            <ToolBarDivider/>

            {/* Refresh points base on view */}
            <ToolBarButton
              title={t('profile.refresh-points-based-on-current-view')}
              icon={"fa-refresh"}
              onClick={reloadPointsProfile}
            />

            {/* Reset zoom and position */}
            <ToolBarButton
              title={t('profile.reset-chart-to-default-zoom-and-position')}
              icon={"fa-bar-chart"}
              onClick={resetPointsProfileView}
            />

            {/* Download profile */}
            <ToolBarButton
              title={t('profile.save-profile-chart-to-image-file')}
              icon={"fa-download"}
              onClick={savePointProfileImage}
            />

          </IconToolBar>

          <div className={classes.profileChart}>
            <canvas ref={onRefModified} id="profile-chart"/>
          </div>

        </div>
      </DraggableDialog>
    </React.Fragment>
  );
});